import React from 'react';
import { ArrowUp, ArrowDown, Trophy } from 'lucide-react';
import { useLeaderboard } from '../hooks/useLeaderboardData';
import { RankBadge } from './RankBadge';
import { formatCurrency, formatNumber } from '../utils/formatters';

export function TopBridgeUsers() {
  const { 
    data: toShidoData, 
    loading: toShidoLoading, 
    error: toShidoError 
  } = useLeaderboard({ filter: 'to_shido', limit: 3 });

  const { 
    data: fromShidoData, 
    loading: fromShidoLoading, 
    error: fromShidoError 
  } = useLeaderboard({ filter: 'from_shido', limit: 3 });

  const handleUserClick = (address: string) => {
    window.location.hash = `/user/${address}`;
  };

  const renderUserList = (
    users: any[], 
    loading: boolean, 
    error: string | null, 
    title: string, 
    icon: React.ReactNode,
    colorClass: string
  ) => (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-4">
        {icon}
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="loading-shimmer h-12 rounded-lg"></div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-4 text-red-400 text-sm">
          Failed to load data
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-4 text-slate-400 text-sm">
          No users yet
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user, index) => (
            <div
              key={user.address}
              onClick={() => handleUserClick(user.address)}
              className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <RankBadge rank={index + 1} />
                <div>
                  <div className={`font-mono text-sm group-hover:${colorClass} transition-colors text-white`}>
                    {user.displayAddress}
                  </div>
                  <div className="text-xs text-slate-400">
                    {formatNumber(user.transactionCount)} txs
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-bold ${colorClass}`}>
                  {formatCurrency(user.totalVolume)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-8 animate-slide-up">
      <div className="flex items-center justify-center gap-3 mb-6">
        <Trophy className="w-6 h-6 text-yellow-400" />
        <h2 className="text-2xl font-bold text-white">Top Bridge Champions</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderUserList(
          toShidoData,
          toShidoLoading,
          toShidoError,
          "To Shido",
          <ArrowUp className="w-5 h-5 text-green-400" />,
          "text-green-400"
        )}
        
        {renderUserList(
          fromShidoData,
          fromShidoLoading,
          fromShidoError,
          "From Shido",
          <ArrowDown className="w-5 h-5 text-red-400" />,
          "text-red-400"
        )}
      </div>
    </div>
  );
};
